import { useState, type ReactNode } from 'react';

import { Button } from '../../button';

import {
  ATTEMPT_CAP,
  attemptLine,
  canRetry,
  costBreakdown,
  costSummary,
  EXCHANGE_MAX_TOKENS,
  exchange,
  exchangeSummary,
  failureByKind,
  failures,
  failuresSummary,
  failuresTransition,
  FINISH_MAX_TOKENS,
  finishCompletion,
  finishSummary,
  initialCost,
  initialExchange,
  initialFailures,
  initialFinish,
  lastAttempt,
  parseOutcome,
  policyLabels,
  replyBody,
  replyLengths,
  replyTexts,
  REQUESTS_PER_DAY,
  requestBody,
  responseText,
  toExchangeMaxTokens,
  toFailureKind,
  toFinishMaxTokens,
  toNoteId,
  toReplyKind,
  toReplyLength,
  toRequestsPerDay,
  totalWait,
  type FailuresAction,
} from './api-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import { lessonNotes } from './model-models';

import './relationship-labs.css';
import './api-labs.css';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
const noteOptions = lessonNotes.map((note) => ({
  value: String(note.id),
  label: `Note ${note.id}: ${note.title}`,
}));

export function ApiExchangeLab() {
  const [state, setState] = useState(initialExchange);
  const result = exchange(state);
  const summary = exchangeSummary(state);
  return (
    <Lab
      title="What exactly leaves our program, and what comes back?"
      eyebrow="API model · one exchange"
      className="rel-lab api-lab"
    >
      <Predict>
        What changes in the reply without the system message? Uncheck Include the system message,
        then set max_tokens to 20.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: toNoteId(value) }))
            }
            options={noteOptions}
          />
        </label>
        <Toggle
          label="Include the system message"
          checked={state.includeSystem}
          onChange={() =>
            setState((current) => ({ ...current, includeSystem: !current.includeSystem }))
          }
        />
        <label>
          max_tokens
          <ModelSelect
            aria-label="max_tokens"
            value={String(state.maxTokens)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, maxTokens: toExchangeMaxTokens(value) }))
            }
            options={EXCHANGE_MAX_TOKENS.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialExchange())} />
      </div>
      <div className="cb-two">
        <Panel label="Request body">
          <pre aria-label="Request body">{requestBody(state)}</pre>
        </Panel>
        <Panel label="Reply body">
          <pre aria-label="Reply body">{replyBody(state)}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Messages sent', String(result.messages.length)],
          ['Prompt tokens', String(result.promptTokens)],
          ['Completion tokens', String(result.completion.completionTokens)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">A simulation of one API exchange.</p>
    </Lab>
  );
}

export function ApiFinishReasonsLab() {
  const [state, setState] = useState(initialFinish);
  const completion = finishCompletion(state);
  const summary = finishSummary(state);
  return (
    <Lab
      title="Why is the reply cut, and what breaks when it is?"
      eyebrow="API model · finish_reason"
      className="rel-lab api-lab"
    >
      <Predict>
        Will json.loads parse a reply cut at 20 tokens? Read json.loads(content), then set
        max_tokens to 80.
      </Predict>
      <div className="cb-controls">
        <label>
          Reply
          <ModelSelect
            aria-label="Reply"
            value={state.reply}
            onValueChange={(value) =>
              setState((current) => ({ ...current, reply: toReplyKind(value) }))
            }
            options={(['sentence', 'json'] as const).map((kind) => ({
              value: kind,
              label: replyTexts[kind].label,
            }))}
          />
        </label>
        <label>
          max_tokens
          <ModelSelect
            aria-label="max_tokens"
            value={String(state.maxTokens)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, maxTokens: toFinishMaxTokens(value) }))
            }
            options={FINISH_MAX_TOKENS.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialFinish())} />
      </div>
      <Panel label="Reply">
        <pre aria-label="Reply content">{completion.content}</pre>
      </Panel>
      <Facts
        values={[
          ['finish_reason', completion.finishReason],
          ['completion_tokens', String(completion.completionTokens)],
          ['json.loads(content)', parseOutcome(state)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">A simulation of a cut reply.</p>
    </Lab>
  );
}

export function ApiFailuresLab() {
  const [state, setState] = useState(initialFailures);
  const act = (action: FailuresAction) =>
    setState((current) => failuresTransition(current, action));
  const spec = failureByKind(state.failure);
  const last = lastAttempt(state);
  const summary = failuresSummary(state);
  return (
    <Lab
      title="Which failures should a program retry?"
      eyebrow="API model · failures"
      className="rel-lab api-lab"
    >
      <Predict>
        Will a retry fix a 503? Press Send the request, then Retry twice. Repeat with 401 wrong
        token.
      </Predict>
      <div className="cb-controls">
        <label>
          Failure
          <ModelSelect
            aria-label="Failure"
            value={state.failure}
            onValueChange={(value) => act({ type: 'failure', value: toFailureKind(value) })}
            options={failures.map((failure) => ({ value: failure.kind, label: failure.label }))}
          />
        </label>
        <Button size="sm" onClick={() => act({ type: 'send' })}>
          Send the request
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!canRetry(state)}
          onClick={() => act({ type: 'retry' })}
        >
          Retry
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="Attempts">
          <pre aria-label="Attempts">
            {state.attempts.length
              ? state.attempts.map(attemptLine).join('\n')
              : '(nothing sent yet)'}
          </pre>
        </Panel>
        <Panel label="Last response">
          <pre aria-label="Response body">{last ? responseText(last) : '(nothing sent yet)'}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Attempts', `${state.attempts.length} of at most ${ATTEMPT_CAP}`],
          ['Total wait', `${totalWait(state)} s`],
          ['Policy', policyLabels[spec.policy]],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">A simulation of API failures.</p>
    </Lab>
  );
}

export function ApiCostLab() {
  const [state, setState] = useState(initialCost);
  const cost = costBreakdown(state);
  const summary = costSummary(state);
  const shares: [string, number, string][] = [
    ['Input', cost.inputShare, cost.inputCost],
    ['Output', cost.outputShare, cost.outputCost],
  ];
  return (
    <Lab
      title="What does the feature cost per day?"
      eyebrow="API model · cost"
      className="rel-lab api-lab"
    >
      <Predict>
        What share of the bill will a longer reply take? Set Reply length to A paragraph, about 120
        tokens.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: toNoteId(value) }))
            }
            options={noteOptions}
          />
        </label>
        <label>
          Reply length
          <ModelSelect
            aria-label="Reply length"
            value={state.reply}
            onValueChange={(value) =>
              setState((current) => ({ ...current, reply: toReplyLength(value) }))
            }
            options={(['line', 'paragraph'] as const).map((length) => ({
              value: length,
              label: replyLengths[length].label,
            }))}
          />
        </label>
        <label>
          Requests per day
          <ModelSelect
            aria-label="Requests per day"
            value={String(state.perDay)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, perDay: toRequestsPerDay(value) }))
            }
            options={REQUESTS_PER_DAY.map((count) => ({
              value: String(count),
              label: String(count),
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialCost())} />
      </div>
      <Panel label="Share of each request's cost">
        <ol className="api-shares" aria-label="Cost share">
          {shares.map(([label, share, amount]) => (
            <li key={label}>
              <span>{label}</span>
              <span className="api-bar" aria-hidden="true">
                <span style={{ width: `${share}%` }} />
              </span>
              <span>
                {share}% · {amount}
              </span>
            </li>
          ))}
        </ol>
      </Panel>
      <Facts
        values={[
          ['Tokens per request', `${cost.inputTokens} in, ${cost.outputTokens} out`],
          ['Cost per request', cost.perRequest],
          ['Cost per day', cost.perDay],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">A simulation of pricing at the example rate.</p>
    </Lab>
  );
}
